import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Loader2, Sparkles, X, ChevronRight, ShieldAlert, Info, AlertTriangle,
  Lightbulb, ExternalLink,
} from 'lucide-react';

const SEV_STYLE = {
  high:   { pill: 'text-sell border-sell', icon: ShieldAlert },
  medium: { pill: 'text-hold border-hold', icon: AlertTriangle },
  low:    { pill: 'text-cyan-sys border-cyan-sys', icon: Info },
};

const CATEGORY_LABEL = {
  dead_code_python:     'Dead code (Python)',
  oversized_jsx:        'Oversized JSX',
  large_pyfile:         'Large Python file',
  missing_testid:       'Missing data-testid',
  unbounded_collection: 'Unbounded collection',
  todo_marker:          'TODO / FIXME marker',
};

/**
 * SelfImprovementSection — Phase 51.H Lab section.
 * Runs a static scan of the codebase and surfaces suggestions bucketed
 * by severity + category. Operator can dismiss suggestions with a note.
 * Read-only in preview; scan/dismiss are production-gated.
 */
export default function SelfImprovementSection({ isProd, onSwitchTab }) {
  const [suggestions, setSuggestions] = useState([]);
  const [lastScan, setLastScan] = useState(null);
  const [filterStatus, setFilterStatus] = useState('open');
  const [filterCategory, setFilterCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [dismissingId, setDismissingId] = useState(null);
  const [creatingPlanId, setCreatingPlanId] = useState(null);
  const [dismissNote, setDismissNote] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(() => {
    const params = new URLSearchParams({ status: filterStatus, limit: '300' });
    if (filterCategory) params.set('category', filterCategory);
    return api.get(`/lab/improve/list?${params.toString()}`)
      .then(({ data }) => {
        setSuggestions(data.suggestions || []);
        setLastScan(data.last_scan || null);
      })
      .catch(() => {});
  }, [filterStatus, filterCategory]);

  useEffect(() => { load(); }, [load]);

  const runScan = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/lab/improve/scan');
      const totals = data.totals || {};
      toast.success(
        `Scan complete · ${Object.values(totals).reduce((a, b) => a + b, 0)} findings`,
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async (id) => {
    setDismissingId(id);
    try {
      await api.post(`/lab/improve/${id}/dismiss`, { note: dismissNote });
      toast.success('Dismissed');
      setDismissNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dismiss failed');
    } finally {
      setDismissingId(null);
    }
  };

  const createPlan = async (id) => {
    setCreatingPlanId(id);
    try {
      const { data } = await api.post(`/lab/improve/${id}/create_plan`);
      toast.success(
        data?.reused
          ? `Reusing existing plan ${data.id.slice(0, 8)}`
          : `Plan created: ${data.id.slice(0, 8)}`,
      );
      load();
      setTimeout(() => onSwitchTab?.('planner'), 600);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Plan creation failed');
    } finally {
      setCreatingPlanId(null);
    }
  };

  const grouped = useMemo(() => {
    const g = { high: [], medium: [], low: [] };
    suggestions.forEach((s) => (g[s.severity] || g.low).push(s));
    return g;
  }, [suggestions]);

  const selected = useMemo(
    () => suggestions.find((s) => s.id === selectedId),
    [suggestions, selectedId],
  );

  const categories = useMemo(() => {
    const set = new Set(suggestions.map((s) => s.category));
    return Array.from(set).sort();
  }, [suggestions]);

  return (
    <div data-testid="lab-improve" className="space-y-3">
      {/* HEADER */}
      <div className="border border-cyan-sys p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-1">
              // self-improvement engine
            </div>
            <div className="text-[10px] text-[var(--txt-secondary)]">
              {lastScan
                ? <>Last scan {(lastScan.finished_at || '').slice(0, 19).replace('T', ' ')}Z · by {lastScan.author} · {Object.values(lastScan.totals || {}).reduce((a, b) => a + b, 0)} findings</>
                : 'No scans yet.'}
            </div>
          </div>
          <button
            data-testid="lab-improve-scan"
            onClick={runScan}
            disabled={!isProd || busy}
            className="flex items-center gap-1.5 border border-cyan-sys text-cyan-sys px-3 py-2 text-[10px] uppercase tracking-[0.2em] hover:bg-cyan-sys hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
            {busy ? 'scanning...' : 'run scan'}
          </button>
        </div>
        {!isProd && (
          <div className="text-[10px] text-[var(--txt-secondary)] mt-2">
            ⚠ Scan action is production-only. You can browse existing suggestions here.
          </div>
        )}
      </div>

      {/* FILTERS */}
      <div className="flex flex-wrap gap-1.5 items-center text-[10px]">
        <span className="text-[var(--txt-secondary)] uppercase tracking-[0.2em] mr-1">status:</span>
        {['open', 'dismissed', 'resolved', 'all'].map((s) => (
          <button
            key={s}
            data-testid={`lab-improve-filter-${s}`}
            onClick={() => setFilterStatus(s)}
            className={`px-2 py-0.5 border uppercase tracking-[0.15em] ${
              filterStatus === s
                ? 'border-cyan-sys text-cyan-sys'
                : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:border-cyan-sys'
            }`}
          >
            {s}
          </button>
        ))}
        <span className="text-[var(--txt-secondary)] uppercase tracking-[0.2em] mx-1 ml-3">category:</span>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="bg-transparent border border-[var(--border-default)] px-2 py-0.5 text-[10px] font-mono focus:border-cyan-sys outline-none"
        >
          <option value="">all</option>
          {categories.map((c) => (
            <option key={c} value={c}>{CATEGORY_LABEL[c] || c}</option>
          ))}
        </select>
      </div>

      {/* LIST + DETAIL */}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="space-y-3">
          {suggestions.length === 0 ? (
            <div className="border border-[var(--border-default)] p-4 text-[11px] text-[var(--txt-secondary)]">
              {lastScan
                ? 'Nothing to improve here — great job.'
                : 'No scan has ever run. Click "run scan" to see findings.'}
            </div>
          ) : (
            ['high', 'medium', 'low'].map((sev) => (
              grouped[sev].length > 0 && (
                <div key={sev} className="space-y-1">
                  <div className={`text-[9px] tracking-[0.25em] uppercase ${SEV_STYLE[sev].pill.split(' ')[0]} flex items-center gap-1`}>
                    {React.createElement(SEV_STYLE[sev].icon, { size: 10 })}
                    {sev} ({grouped[sev].length})
                  </div>
                  {grouped[sev].map((s) => (
                    <button
                      key={s.id}
                      data-testid={`lab-improve-item-${s.id}`}
                      onClick={() => setSelectedId(s.id)}
                      className={`w-full text-left border p-2 transition-colors ${
                        s.id === selectedId
                          ? 'border-cyan-sys bg-[var(--bg-panel-lighter)]'
                          : 'border-[var(--border-default)] hover:border-cyan-sys'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-0.5">
                        <span className="text-[11px] text-[var(--txt-primary)] truncate font-mono">
                          {s.title}
                        </span>
                        <ChevronRight size={11} className="text-[var(--txt-secondary)] mt-0.5 shrink-0" />
                      </div>
                      <div className="text-[9px] text-[var(--txt-secondary)] font-mono">
                        {CATEGORY_LABEL[s.category] || s.category}
                        {s.status !== 'open' && <> · <span className="uppercase">{s.status}</span></>}
                      </div>
                    </button>
                  ))}
                </div>
              )
            ))
          )}
        </div>

        {/* DETAIL */}
        <div className="border border-[var(--border-default)] p-3 min-h-[420px]">
          {!selected ? (
            <div className="text-[11px] text-[var(--txt-secondary)]">Select a suggestion to inspect.</div>
          ) : (
            <div className="space-y-3 text-[11px]">
              <div className="flex items-center justify-between">
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys">// suggestion</div>
                <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 border ${SEV_STYLE[selected.severity]?.pill || ''}`}>
                  {selected.severity}
                </span>
              </div>

              <div className="text-[var(--txt-primary)] font-medium">{selected.title}</div>

              <div className="text-[10px] text-[var(--txt-secondary)]">
                <span className="uppercase tracking-[0.15em]">rule:</span> {selected.rule}
                {' · '}
                <span className="uppercase tracking-[0.15em]">status:</span> {selected.status}
              </div>

              <div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-0.5">// target</div>
                <div className="font-mono text-[var(--txt-primary)] break-all">{selected.target}</div>
              </div>

              <div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-0.5">// detail</div>
                <div className="text-[var(--txt-primary)] whitespace-pre-wrap">{selected.detail}</div>
              </div>

              {selected.action && (
                <div>
                  <div className="text-[9px] tracking-[0.25em] uppercase text-cyan-sys mb-0.5">// suggested action</div>
                  <pre className="p-2 text-[10px] font-mono text-buy whitespace-pre overflow-x-auto border border-[var(--border-default)] bg-[var(--bg-panel-lighter)]">
                    {selected.action}
                  </pre>
                </div>
              )}

              {selected.dismissed_by && (
                <div className="border border-[var(--border-default)] p-2 text-[10px]">
                  <div className="text-[var(--txt-secondary)] uppercase tracking-[0.15em] mb-0.5">
                    dismissed by {selected.dismissed_by}
                  </div>
                  <div className="text-[var(--txt-primary)]">
                    {selected.dismiss_note || '(no note)'}
                  </div>
                </div>
              )}

              {selected.linked_plan_id && (
                <div className="border border-buy p-2 text-[10px] flex items-center justify-between gap-2">
                  <div>
                    <div className="text-buy uppercase tracking-[0.15em] mb-0.5">
                      linked plan
                    </div>
                    <div className="font-mono text-[var(--txt-primary)]">
                      {selected.linked_plan_id}
                    </div>
                  </div>
                  <button
                    data-testid="lab-improve-open-plan"
                    onClick={() => onSwitchTab?.('planner')}
                    className="flex items-center gap-1 border border-buy text-buy px-2 py-1 uppercase tracking-[0.2em] hover:bg-buy hover:text-black"
                  >
                    <ExternalLink size={11} /> open
                  </button>
                </div>
              )}

              {selected.status === 'open' && (
                <div className="pt-2 border-t border-[var(--border-default)] space-y-1.5">
                  {!selected.linked_plan_id && (
                    <button
                      data-testid="lab-improve-create-plan"
                      onClick={() => createPlan(selected.id)}
                      disabled={!isProd || creatingPlanId === selected.id}
                      className="w-full flex items-center justify-center gap-1.5 border border-buy text-buy px-3 py-2 text-[10px] uppercase tracking-[0.2em] hover:bg-buy hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {creatingPlanId === selected.id
                        ? <Loader2 size={11} className="animate-spin" />
                        : <Lightbulb size={11} />}
                      {creatingPlanId === selected.id ? 'generating plan...' : 'create improvement plan'}
                    </button>
                  )}
                  <input
                    data-testid="lab-improve-dismiss-note"
                    value={dismissNote}
                    onChange={(e) => setDismissNote(e.target.value)}
                    placeholder="dismiss reason (optional)"
                    disabled={!isProd}
                    className="w-full bg-transparent border border-[var(--border-default)] px-2 py-1.5 text-[10px] font-mono focus:border-cyan-sys outline-none"
                  />
                  <button
                    data-testid="lab-improve-dismiss"
                    onClick={() => dismiss(selected.id)}
                    disabled={!isProd || dismissingId === selected.id}
                    className="flex items-center gap-1 border border-sell text-sell px-2 py-1 text-[10px] uppercase tracking-[0.2em] hover:bg-sell hover:text-black disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    {dismissingId === selected.id
                      ? <Loader2 size={11} className="animate-spin" />
                      : <X size={11} />}
                    dismiss
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
